import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import DraftIcon from '@mui/icons-material/EditNote';

interface PostSummary {
  id: string;
  title: string;
  excerpt: string;
  authorName: string;
  authorPhotoURL?: string;
  authorId: string;
  createdAt: Timestamp | null;
  published: boolean;
}

function formatDate(ts: Timestamp | null): string {
  if (!ts) return '';
  return ts.toDate().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function PostCardSkeleton() {
  return (
    <Card variant="outlined">
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="100%" />
        <Skeleton variant="text" width="80%" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="text" width={120} />
        </Box>
      </CardContent>
    </Card>
  );
}

export default function BlogPage() {
  const { currentUser, authUser } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = React.useState<PostSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const isAdmin = currentUser?.role === 'admin';
  const canPost = isAdmin || currentUser?.role === 'author';

  React.useEffect(() => {
    let cancelled = false;
    const role = currentUser?.role;
    const uid = authUser?.uid;

    async function fetchPosts() {
      setLoading(true);
      setError('');
      try {
        const canSeeUnpublished = role === 'admin' || role === 'author';
        const q = canSeeUnpublished
          ? query(collection(db, 'posts'), orderBy('createdAt', 'desc'))
          : query(collection(db, 'posts'), where('published', '==', true), orderBy('createdAt', 'desc'));

        const snapshot = await getDocs(q);
        if (cancelled) return;

        setPosts(
          snapshot.docs
            .map(d => {
              const data = d.data();
              return {
                id: d.id,
                title: data.title ?? '(untitled)',
                excerpt: data.excerpt ?? '',
                authorName: data.authorName ?? 'Unknown',
                authorPhotoURL: data.authorPhotoURL,
                authorId: data.authorId ?? '',
                createdAt: data.createdAt ?? null,
                published: data.published ?? false,
              };
            })
            .filter(p => p.published || role === 'admin' || p.authorId === uid)
        );
      } catch (err) {
        if (!cancelled) setError('Failed to load posts. Please refresh.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPosts();
    return () => { cancelled = true; };
  }, [currentUser?.role, authUser?.uid]);

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 4 }, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Blog
        </Typography>
        {canPost && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/blog/new')}
          >
            New post
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <PostCardSkeleton key={i} />)
          : posts.length === 0
            ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">
                  No posts yet.{canPost ? ' Be the first to write one.' : ''}
                </Typography>
              </Box>
            )
            : posts.map(post => (
              <Card key={post.id} variant="outlined" sx={!post.published ? { borderStyle: 'dashed' } : undefined}>
                <CardActionArea component={RouterLink} to={`/blog/${post.id}`}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {post.title}
                      </Typography>
                      {!post.published && (
                        <Chip
                          icon={<DraftIcon />}
                          label="Draft"
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      )}
                    </Box>
                    {post.excerpt && (
                      <Typography variant="body2" color="text.secondary">
                        {post.excerpt}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Avatar src={post.authorPhotoURL} sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                        {post.authorName[0]?.toUpperCase()}
                      </Avatar>
                      <Typography variant="caption" color="text.secondary">
                        {post.authorName}
                      </Typography>
                      {post.createdAt && (
                        <>
                          <Typography variant="caption" color="text.secondary" aria-hidden>·</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(post.createdAt)}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))
        }
      </Box>
    </Box>
  );
}
